import { useCallback, useEffect, useMemo, useState } from "react";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutDashboard, Users, School, Megaphone, CalendarDays, UserPlus, Rocket, CalendarClock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MarketingSummary } from "@/components/marketing/MarketingSummary";
import { MarketingOverview } from "@/components/marketing/MarketingOverview";
import { ContactPanel } from "@/components/marketing/ContactPanel";
import { CampaignsPanel } from "@/components/marketing/CampaignsPanel";
import { ContentPanel } from "@/components/marketing/ContentPanel";
import { influencerFields, schoolFields } from "@/components/marketing/marketingFields";
import { api } from "@/lib/api";
import { INF_STAGES, SCHOOL_STAGES, INF_CLOSED, SCHOOL_CLOSED, INF_INTERACTIONS, SCHOOL_INTERACTIONS, buildCodes } from "@/data/marketingOptions";

const INF_DETAIL = [
  ["Platform", "platform"], ["Audience", "audience_size"], ["Relevance", "relevance"],
  ["Discount code", "discount_code"], ["Agreed fee (£)", "agreed_fee"], ["Commission %", "commission_pct"],
  ["Est. reach", "estimated_reach"], ["Link clicks", "link_clicks"], ["Sign-ups", "prelaunch_signups"],
  ["Paid subs", "paid_subscriptions"], ["Revenue (£)", "revenue_attributed"], ["Next follow-up", "next_follow_up_date"],
];
const SCHOOL_DETAIL = [
  ["Type", "school_type"], ["Region", "region"], ["Country", "country"], ["Pupils", "num_pupils"],
  ["Age range", "target_age_range"], ["Pilot interest", "pilot_interest"], ["Pilot pupils", "num_pilot_pupils"],
  ["Contact role", "contact_role"], ["Next follow-up", "next_follow_up_date"],
];

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["influencers", "Influencers", Users],
  ["schools", "Schools", School],
  ["campaigns", "Campaigns", Megaphone],
  ["content", "Content Calendar", CalendarDays],
];

const Highlight = ({ icon: Icon, label, value, color, to }) => (
  <Link to={to} data-testid={`hero-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} aria-label={`${label} — open destination`} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/15 backdrop-blur-sm transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60">
    <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: color }}>
      <Icon size={18} strokeWidth={2.3} className="text-white" />
    </span>
    <div className="leading-tight">
      <p className="font-numeric text-2xl font-bold text-white">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70">{label}</p>
    </div>
  </Link>
);

function MarketingHero() {
  const [s, setS] = useState(null);
  useEffect(() => { api.list("marketing-summary").then(setS).catch(() => {}); }, []);
  return (
    <section data-testid="marketing-hero" className="relative mb-8 overflow-hidden rounded-3xl border border-[#E4E9F3] p-6 shadow-[0_8px_30px_rgba(20,36,95,0.12)] sm:p-8"
      style={{ background: "linear-gradient(120deg, #0B3B48 0%, #0EA5A4 45%, #7C3AED 100%)" }}>
      <span className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#EC4899]/30 blur-3xl" />
      <span className="pointer-events-none absolute -bottom-14 left-1/3 h-40 w-40 rounded-full bg-[#22D3EE]/25 blur-3xl" />
      <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#67E8F9]">Growth Engine</span>
          <h1 className="mt-1.5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">Marketing &amp; Growth</h1>
          <p className="mt-2 max-w-md text-sm text-white/80">Building awareness, partnerships and launch momentum.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Highlight icon={UserPlus} label="Sign-ups" value={s?.prelaunch_signups ?? "—"} color="#22D3EE" to="/marketing?tab=overview" />
          <Highlight icon={Rocket} label="Active campaigns" value={s?.active_campaigns ?? "—"} color="#0EA5A4" to="/marketing?tab=campaigns&status=Active" />
          <Highlight icon={CalendarClock} label="Follow-ups due" value={s?.influencer_followups_due ?? "—"} color="#FB6F5A" to="/marketing?tab=influencers&filter=followup-due" />
        </div>
      </div>
    </section>
  );
}

export default function MarketingGrowth() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bump, setBump] = useState(0);
  const [influencers, setInfluencers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const refresh = useCallback(() => setBump((b) => b + 1), []);

  const tabParam = searchParams.get("tab");
  const tab = TABS.some((t) => t[0] === tabParam) ? tabParam : "overview";
  const changeTab = (v) => { const p = new URLSearchParams(searchParams); p.set("tab", v); ["filter", "view", "influencer", "school", "campaign", "content"].forEach((k) => p.delete(k)); setSearchParams(p); };

  const load = useCallback(async () => {
    try { const [inf, camps] = await Promise.all([api.list("influencers"), api.list("marketing-campaigns")]); setInfluencers(inf); setCampaigns(camps); }
    catch { setInfluencers([]); setCampaigns([]); }
  }, []);
  useEffect(() => { load(); }, [load, bump]);

  const codes = useMemo(() => buildCodes(influencers, campaigns), [influencers, campaigns]);

  return (
    <>
      <MarketingHero />

      <div className="mb-8"><MarketingSummary refreshKey={bump} /></div>

      <ResetFiltersButton /><Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList data-testid="marketing-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm">
          {TABS.map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:[background-color:var(--accent-primary)] sm:px-4">
              <Icon size={15} strokeWidth={2.2} /><span className="hidden xs:inline sm:inline">{l}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><MarketingOverview refreshKey={bump} codes={codes} /></TabsContent>
        <TabsContent value="influencers">
          <ContactPanel resource="influencers" stages={INF_STAGES} closed={INF_CLOSED} fields={influencerFields()} interactionTypes={INF_INTERACTIONS} detailKeys={INF_DETAIL} addDefaults={{ platform: "Instagram", relevance: "3" }} area="Influencer" onChange={refresh} />
        </TabsContent>
        <TabsContent value="schools">
          <ContactPanel resource="schools" stages={SCHOOL_STAGES} closed={SCHOOL_CLOSED} fields={schoolFields()} interactionTypes={SCHOOL_INTERACTIONS} detailKeys={SCHOOL_DETAIL} addDefaults={{ school_type: "Primary school", pilot_interest: "Unknown" }} area="School" onChange={refresh} />
        </TabsContent>
        <TabsContent value="campaigns"><CampaignsPanel codes={codes} onChange={refresh} /></TabsContent>
        <TabsContent value="content"><ContentPanel onChange={refresh} /></TabsContent>
      </Tabs>
    </>
  );
}
